'use client';

import dynamic from 'next/dynamic';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { AlertPopup } from '../components/alert-popup/alert-popup';
import { ControlsPanel } from '../components/controls-panel';
import { Header } from '../components/header/header';
import { LicenseModal } from '../components/header/license-modal';
import { HistorySlider } from '../components/history-slider/history-slider';
import { InfoPanel } from '../components/info-panel/info-panel';
import { LoadingOverlay } from '../components/loading-overlay/loading-overlay';
import { QualityPanel } from '../components/quality-panel';
import { RadLoadingIndicator } from '../components/rad-loading-indicator';
import { RoomNavigator } from '../components/room-navigator/room-navigator';
import { SyncToggle } from '../components/sync/sync-toggle';
import { TimeSlipOverlay } from '../components/time-slip-overlay/time-slip-overlay';
import { Toast } from '../components/toast/toast';
import { AppProvider, useAppContext } from '../hooks/use-app-context';
import type { Room } from '../types/room';
import { loadRooms, getInitialRoomId, getInitialHistoryId } from '../utils/room-loader';

// IntegratedViewerを動的インポート（SSRを無効化）
const DynamicIntegratedViewer = dynamic(
  () => import('../components/integrated-viewer/integrated-viewer').then((mod) => mod.IntegratedViewer),
  {
    ssr: false,
    loading: () => <LoadingOverlay loading={true} message='空間データを読み込み中...' />
  }
);

/**
 * 点群ビューワーアプリのメインコンポーネント
 */
function PointCloudViewerApp() {
  const { state, dispatch, toggleSync, toggleUiHidden, hideToast, showToast } = useAppContext();
  const { isSyncEnabled, isLoading, isRadLoading, isToastVisible, toastMessage, isUiHidden } = state;

  // isRadLoading の変化をログ出力
  useEffect(() => {
    console.log('[PAGE] isRadLoading changed:', isRadLoading);
  }, [isRadLoading]);
  const [showInfo, setShowInfo] = useState(false);
  const [showLicense, setShowLicense] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showQuality, setShowQuality] = useState(false);
  const [isMovingToPast, setIsMovingToPast] = useState(true);
  // 情報パネルが一度閉じられたかどうかを追跡
  const [infoClosedOnce, setInfoClosedOnce] = useState(false);

  // エラーアラート用の状態
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // searchParamsを一度だけ取得（Suspense内でラップ）
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);

  // Uキーでのスクリーンショットモード切り替え
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 入力フィールドにフォーカスがある場合は無視
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        toggleUiHidden();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleUiHidden]);

  // クライアントサイドでのみ実行
  useEffect(() => {
    // ブラウザ環境でのみ実行
    if (typeof window !== 'undefined') {
      setSearchParams(new URLSearchParams(window.location.search));

      // グローバルな未処理のPromiseエラーをキャッチするハンドラーを追加
      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        // Worker terminate エラーをキャッチ（SparkJS内部の正常なクリーンアップ処理）
        if (event.reason && event.reason.message?.includes('Worker terminate')) {
          console.warn('[SparkJS] Worker terminated during cleanup (expected behavior in dev mode)');
          event.preventDefault(); // エラー表示を抑制
          return;
        }

        // No target エラーをキャッチ（SparkJS内部のレンダリング処理）
        if (event.reason && event.reason.message?.includes('No target')) {
          console.warn('[SparkJS] No target error during rendering (expected behavior in dev mode)');
          event.preventDefault(); // エラー表示を抑制
          return;
        }

        console.error('Unhandled Promise Rejection:', event.reason);

        // 403 Forbiddenエラーをキャッチして、アラートを表示
        if (event.reason && (event.reason.message?.includes('403') || event.reason.message?.includes('Forbidden'))) {
          console.log('Caught 403 Forbidden error, showing alert');
          setErrorMessage('この空間は存在しないか、削除されています');
          setShowErrorAlert(true);

          // ローディング状態を解除
          dispatch({ type: 'SET_LOADING', payload: false });

          // 空間履歴切り替え状態をリセット（タイムスリップオーバーレイを非表示にする）
          dispatch({ type: 'SET_HISTORY_SLIDER_CHANGE', payload: false });
          console.log('[HISTORY_SLIDER_STATE] Reset isHistorySliderChange due to 403 error');

          // イベントをキャンセルしてエラーが伝播しないようにする
          event.preventDefault();
        }
      };

      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      // クリーンアップ関数
      return () => {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      };
    }
  }, [dispatch]);

  // ユーザーが手動でトグルを操作したかどうかを追跡するref
  const userToggledRef = useRef(false);
  // 前回のルームパラメータを保存するref
  const prevRoomRef = useRef<string | null>(null);
  // 強制追従コマンド送信用のref
  const forceSyncSenderRef = useRef<(() => void) | null>(null);

  // トグル操作をラップする関数
  const handleToggleSync = useCallback(() => {
    userToggledRef.current = true; // ユーザーが手動でトグルを操作したことを記録
    console.log('[USER_TOGGLE] User manually toggled sync');
    toggleSync();
  }, [toggleSync]);

  // typeパラメータに基づいて同期機能を制御する（初回のみ、またはユーザーが手動で操作していない場合）
  useEffect(() => {
    if (!searchParams) return;

    const typeParam = searchParams.get('type');
    const roomParam = searchParams.get('room'); // ルームパラメータを取得
    const shouldSync = typeParam === 'host' || typeParam === 'guest';

    // ユーザーが手動でトグルを操作した場合は、URLパラメータに基づく制御を無視
    if (userToggledRef.current) {
      console.log('[URL_SYNC] Ignoring URL parameters because user manually toggled sync');
      return;
    }

    // ルームパラメータのみが変更された場合は、同期状態を変更しない
    // この部分が重要: ルーム変更時に同期状態が変わらないようにする
    if (prevRoomRef.current !== roomParam && prevRoomRef.current !== null) {
      console.log('[URL_SYNC] Room parameter changed, keeping current sync state');
      prevRoomRef.current = roomParam;
      return;
    }
    prevRoomRef.current = roomParam;

    // 同期状態の変更が必要な場合のみログを出力
    if ((shouldSync && !isSyncEnabled) || (!shouldSync && isSyncEnabled)) {
      console.log(
        '[URL_SYNC] typeParam:',
        typeParam,
        'shouldSync:',
        shouldSync,
        'current isSyncEnabled:',
        isSyncEnabled
      );
    }

    // 同期が必要なモード（hostまたはguest）で同期が無効な場合は有効にする
    if (shouldSync && !isSyncEnabled) {
      console.log('[SYNC_ENABLE] Enabling sync based on URL parameters:', typeParam);
      toggleSync(); // 同期機能を有効にする
    }
    // 同期が不要なモード（noneまたはsoloまたは未指定）で同期が有効な場合は無効にする
    else if (!shouldSync && isSyncEnabled) {
      console.log('[SYNC_DISABLE] Disabling sync based on URL parameters:', typeParam);
      toggleSync(); // 同期機能を無効にする
    }
  }, [searchParams, isSyncEnabled, toggleSync]);

  // 同期状態が変わったときにログを出力
  useEffect(() => {
    console.log('[SYNC_STATE] Sync state changed to:', isSyncEnabled);
  }, [isSyncEnabled]);

  // typeパラメータを取得
  const typeParam = searchParams?.get('type');
  // エリア・モードパラメータを取得
  const modeParam = searchParams?.get('mode');

  // ホストモードかどうか（typeパラメータが'host'の場合のみホスト）
  const isHost = typeParam === 'host';
  // プレビューモード判定
  const isPreviewMode = modeParam === 'preview';
  // 同期チャンネル名（roomIdパラメータを使用）
  const channelName = searchParams?.get('roomId') || 'default-room';

  // ブラウザのタブタイトルを更新するヘルパー関数
  const updatePageTitle = useCallback(async (roomId: string, rooms: Room[]) => {
    const currentRoom = rooms.find((r) => r.id === roomId);
    if (!currentRoom) {
      document.title = 'Point Cloud Viewer';
      return;
    }

    try {
      // エリア設定を読み込み
      const response = await fetch('/data/area-config.json');
      if (response.ok) {
        const areaConfig = await response.json();
        const areaName = currentRoom.area ? areaConfig[currentRoom.area]?.name : null;

        // エリア名を優先、なければルーム名
        document.title = areaName || currentRoom.name;
      } else {
        // エリア設定が読み込めない場合はルーム名
        document.title = currentRoom.name;
      }
    } catch (error) {
      console.error('Failed to load area config:', error);
      document.title = currentRoom.name;
    }
  }, []);

  // ルームデータの読み込み
  useEffect(() => {
    const fetchRooms = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const roomsData = await loadRooms(searchParams || undefined);
        console.log('Loaded rooms data:', roomsData);
        dispatch({ type: 'SET_ROOMS', payload: roomsData });

        // getInitialRoomId関数を使用してルームIDを取得
        const selectedRoomId = searchParams ? getInitialRoomId(searchParams, roomsData) : roomsData[0]?.id;
        console.log('Selected room ID:', selectedRoomId);

        if (selectedRoomId) {
          // ルームを選択
          dispatch({ type: 'SET_CURRENT_ROOM', payload: selectedRoomId });

          // getInitialHistoryId関数を使用して空間履歴IDを取得
          const selectedRoom = roomsData.find((r) => r.id === selectedRoomId);
          const historyId = getInitialHistoryId(selectedRoom);
          console.log('Selected history ID:', historyId);

          if (historyId) {
            dispatch({ type: 'SET_CURRENT_HISTORY', payload: historyId });
          }

          // 初期ロード時にタブタイトルを更新
          await updatePageTitle(selectedRoomId, roomsData);
        }
      } catch (error) {
        console.error('Failed to load rooms:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    fetchRooms();
  }, [dispatch, searchParams, updatePageTitle]);

  // ルーム変更時にブラウザのタブタイトルを更新
  useEffect(() => {
    if (state.currentRoomId && state.rooms.length > 0) {
      updatePageTitle(state.currentRoomId, state.rooms);
    }
  }, [state.currentRoomId, state.rooms, updatePageTitle]);

  // 情報パネルの表示/非表示を切り替え
  const toggleInfo = () => {
    setShowInfo(!showInfo);
    // 情報パネルを閉じる場合、閉じられたことを記録
    if (showInfo) {
      setInfoClosedOnce(true);
    }
  };

  // 権利表記モーダルの表示/非表示を切り替え
  const toggleLicense = () => {
    setShowLicense(!showLicense);
  };

  // クオリティ調整パネルの表示/非表示を切り替え
  const toggleQuality = () => {
    setShowQuality(!showQuality);
  };

  // 操作ガイドを表示する
  const showOperationGuide = () => {
    setShowControls(true);
  };

  // 操作ガイドを閉じたときの処理
  const handleControlsClose = () => {
    setShowControls(false);
    // 情報パネルが一度も閉じられていない場合のみ表示する
    if (!infoClosedOnce) {
      setShowInfo(true);
    }
  };

  // 強制追従ボタンのハンドラー
  const handleForceSyncClick = useCallback(() => {
    if (forceSyncSenderRef.current) {
      console.log('[FORCE_SYNC] Triggering force sync command from page.tsx');
      forceSyncSenderRef.current();
      showToast('全ゲストの追従をオンにしました');
    }
  }, [showToast]);

  // 強制追従送信関数を登録するコールバック
  const handleRegisterForceSyncSender = useCallback((sender: () => void) => {
    forceSyncSenderRef.current = sender;
    console.log('[FORCE_SYNC] Sender registered in page.tsx');
  }, []);

  return (
    <div className={`point-cloud-viewer ${isPreviewMode ? 'preview-mode' : ''}`}>
      {/* ヘッダー */}
      {!isPreviewMode && !isUiHidden && (
        <Header
          guestMode={typeParam === 'guest'}
          hostMode={typeParam === 'host'}
          onForceSyncClick={handleForceSyncClick}
          onInfoClick={toggleInfo}
          onLicenseClick={toggleLicense}
          onOperationGuideClick={showOperationGuide}
          onQualityClick={toggleQuality}
        />
      )}

      {/* 情報パネル */}
      {!isPreviewMode && !isUiHidden && (
        <InfoPanel
          visible={showInfo}
          onClose={() => {
            setShowInfo(false);
            setInfoClosedOnce(true); // 閉じるボタンでも閉じられたことを記録
          }}
        />
      )}

      {/* 権利表記モーダル（右側表示モード） */}
      {!isPreviewMode && !isUiHidden && (
        <LicenseModal rightSideMode={true} visible={showLicense} onClose={() => setShowLicense(false)} />
      )}

      {/* クオリティ調整パネル */}
      {!isPreviewMode && !isUiHidden && <QualityPanel visible={showQuality} onClose={() => setShowQuality(false)} />}

      {/* 操作ガイド */}
      {!isPreviewMode && !isUiHidden && <ControlsPanel visible={showControls} onClose={handleControlsClose} />}

      {/* 同期切り替えトグル（明示的にtype=guestの場合のみ表示） */}
      {!isPreviewMode && !isUiHidden && <SyncToggle isGuest={typeParam === 'guest'} onToggle={handleToggleSync} />}

      {/* 空間履歴スライダー */}
      {!isPreviewMode && !isUiHidden && (
        <HistorySlider disabled={!isHost && isSyncEnabled} setIsMovingToPast={setIsMovingToPast} />
      )}

      {/* ルーム切り替えボタン（左右矢印）- ホストモードの時のみ表示 */}
      {!isPreviewMode && !isUiHidden && isHost && <RoomNavigator />}

      {/* ローディングオーバーレイ - 初期ロード時のみ表示、history-sliderによる履歴切り替え時は表示しない */}
      <LoadingOverlay loading={isLoading && !state.isHistorySliderChange} message='空間データを読み込み中...' />

      {/* タイムスリップオーバーレイ - history-sliderによる履歴切り替え時のみ表示 */}
      <TimeSlipOverlay isPast={isMovingToPast} visible={state.isHistorySliderChange} />

      {/* RADファイル読み込みインジケーター */}
      <RadLoadingIndicator loading={isRadLoading} />

      {/* トーストUI */}
      <Toast duration={3000} message={toastMessage} visible={isToastVisible} onClose={hideToast} />

      {/* エラーアラート */}
      <AlertPopup message={errorMessage} visible={showErrorAlert} onClose={() => setShowErrorAlert(false)} />

      {/* 統合されたビューワーコンポーネント */}
      <DynamicIntegratedViewer
        key={`integrated-viewer-${String(isHost)}-${state.currentRoomId}`}
        channelName={channelName}
        currentRoom={state.rooms.find((r) => r.id === state.currentRoomId)}
        isGuest={typeParam === 'guest'}
        isHost={isHost}
        isPreviewMode={isPreviewMode}
        isSyncEnabled={isSyncEnabled}
        isUiHidden={isUiHidden}
        currentHistory={state.rooms
          .find((r) => r.id === state.currentRoomId)
          ?.histories.find((h) => h.id === state.currentHistoryId)}
        onRegisterForceSyncSender={handleRegisterForceSyncSender}
      />

      {/* トーストUI */}
      <Toast duration={3000} message={toastMessage} visible={isToastVisible} onClose={hideToast} />
    </div>
  );
}

/**
 * ページコンポーネント
 */
export default function Page() {
  return (
    <Suspense fallback={<LoadingOverlay loading={true} message='空間データを読み込み中...' />}>
      <AppProvider>
        <PointCloudViewerApp />
      </AppProvider>
    </Suspense>
  );
}
